import React, { useState, useEffect, useCallback } from 'react';
import {
  Search, Download, Activity, Users, MessageSquare,
  Settings, Shield, CreditCard, CheckCircle, AlertCircle,
  XCircle, Wrench
} from 'lucide-react';
import { activityService } from '../services/activityService';

const CATEGORY_ICONS = {
  AUTH:         <Users className="w-4 h-4" />,
  CMMS:         <Wrench className="w-4 h-4" />,
  CHATBOT:      <MessageSquare className="w-4 h-4" />,
  SUBSCRIPTION: <CreditCard className="w-4 h-4" />,
  ADMIN:        <Settings className="w-4 h-4" />,
};

const CATEGORY_COLORS = {
  AUTH:         'bg-blue-100 text-blue-700 border-blue-200',
  CMMS:         'bg-orange-100 text-orange-700 border-orange-200',
  CHATBOT:      'bg-purple-100 text-purple-700 border-purple-200',
  SUBSCRIPTION: 'bg-green-100 text-green-700 border-green-200',
  ADMIN:        'bg-gray-100 text-gray-700 border-gray-200',
};

const STATUS_BADGE = {
  SUCCESS: (
    <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
      <CheckCircle className="w-3 h-3" /> Success
    </span>
  ),
  WARNING: (
    <span className="inline-flex items-center gap-1 px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs font-medium">
      <AlertCircle className="w-3 h-3" /> Warning
    </span>
  ),
  FAILURE: (
    <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">
      <XCircle className="w-3 h-3" /> Failed
    </span>
  ),
};

const ActivityLog = () => {
  const [logs, setLogs]               = useState([]);
  const [stats, setStats]             = useState(null);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState('');
  const [page, setPage]               = useState(0);
  const [totalPages, setTotalPages]   = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [filters, setFilters]         = useState({
    category: '', status: '', search: '', dateFrom: '', dateTo: ''
  });
  const [exportLoading, setExportLoading] = useState(false);

  const SIZE = 20;

  const fetchLogs = useCallback(async (pageNum = 0) => {
    setLoading(true);
    setError('');
    try {
      const data = await activityService.getActivity({ page: pageNum, size: SIZE, ...filters });
      setLogs(data.content || []);
      setTotalPages(data.totalPages || 0);
      setTotalElements(data.totalElements || 0);
      setPage(pageNum);
    } catch (err) {
      setError('Failed to load activity logs.');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const fetchStats = useCallback(async () => {
    try {
      const data = await activityService.getStats();
      setStats(data);
    } catch {
      // Stats are non-critical; fail silently
    }
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps -- helper identity changes each render; dependency list is intentional
  useEffect(() => { fetchLogs(0); }, [filters]);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- helper identity changes each render; dependency list is intentional
  useEffect(() => { fetchStats(); }, []);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleExport = async () => {
    setExportLoading(true);
    try {
      const data = await activityService.getActivity({ page: 0, size: 5000, ...filters });
      const rows  = data.content || [];
      const header = 'Timestamp,Category,Event Type,Action,Actor Name,Actor Email,Company,IP,Status';
      const csv    = [header, ...rows.map(r =>
        [r.createdAt, r.category, r.eventType, `"${r.action}"`,
         r.actorName || '', r.actorEmail || '', r.companyName || '',
         r.ipAddress || '', r.status].join(',')
      )].join('\n');

      const blob = new Blob([csv], { type: 'text/csv' });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = `activity-log-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      // Silently ignore export errors
    } finally {
      setExportLoading(false);
    }
  };

  const formatTime = (iso) => {
    if (!iso) return { date: '', time: '' };
    const d = new Date(iso);
    return { date: d.toLocaleDateString(), time: d.toLocaleTimeString() };
  };

  const statsCards = stats ? [
    { name: 'Total Today',  value: stats.totalToday,   color: 'bg-blue-500',   icon: Activity },
    { name: 'Successful',   value: stats.successToday, color: 'bg-green-500',  icon: CheckCircle },
    { name: 'Warnings',     value: stats.warningsToday,color: 'bg-yellow-500', icon: AlertCircle },
    { name: 'Errors',       value: stats.errorsToday,  color: 'bg-red-500',    icon: XCircle },
  ] : [];

  const startIndex = page * SIZE + 1;
  const endIndex   = Math.min((page + 1) * SIZE, totalElements);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Activity Log</h1>
          <p className="text-gray-600 mt-1">Monitor all platform activities and events</p>
        </div>
        <button
          onClick={handleExport}
          disabled={exportLoading}
          className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2 disabled:opacity-50"
        >
          <Download className="w-4 h-4" />
          {exportLoading ? 'Exporting...' : 'Export CSV'}
        </button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {statsCards.map((s) => {
            const Icon = s.icon;
            return (
              <div key={s.name} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 ${s.color} rounded-lg flex items-center justify-center`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <div className="text-sm text-gray-600">{s.name}</div>
                    <div className="text-2xl font-bold text-gray-900">{s.value?.toLocaleString() ?? '—'}</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex flex-col md:flex-row gap-3">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by user, company, or action..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
          <select
            value={filters.category}
            onChange={(e) => handleFilterChange('category', e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
          >
            <option value="">All Categories</option>
            <option value="AUTH">Auth</option>
            <option value="CMMS">CMMS</option>
            <option value="CHATBOT">Chatbot</option>
            <option value="SUBSCRIPTION">Subscription</option>
            <option value="ADMIN">Admin</option>
          </select>
          <select
            value={filters.status}
            onChange={(e) => handleFilterChange('status', e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
          >
            <option value="">All Status</option>
            <option value="SUCCESS">Success</option>
            <option value="WARNING">Warning</option>
            <option value="FAILURE">Failed</option>
          </select>
          <input
            type="date"
            value={filters.dateFrom ? filters.dateFrom.split('T')[0] : ''}
            onChange={(e) => handleFilterChange('dateFrom', e.target.value ? e.target.value + 'T00:00:00' : '')}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
          />
          <input
            type="date"
            value={filters.dateTo ? filters.dateTo.split('T')[0] : ''}
            onChange={(e) => handleFilterChange('dateTo', e.target.value ? e.target.value + 'T23:59:59' : '')}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
          />
        </div>
      </div>

      {/* Log List */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        {error && (
          <div className="p-6 text-red-600 text-sm">{error}</div>
        )}

        {loading ? (
          <div className="p-12 flex justify-center">
            <div className="w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : logs.length === 0 ? (
          <div className="p-12 text-center text-gray-500">No activity found for the selected filters.</div>
        ) : (
          <div className="divide-y divide-gray-100">
            {logs.map((log) => {
              const { date, time } = formatTime(log.createdAt);
              return (
                <div key={log.id} className="p-5 hover:bg-gray-50 transition-colors">
                  <div className="flex items-start gap-4">
                    <div className={`w-10 h-10 border rounded-lg flex items-center justify-center flex-shrink-0 ${CATEGORY_COLORS[log.category] || CATEGORY_COLORS.ADMIN}`}>
                      {CATEGORY_ICONS[log.category] || <Activity className="w-4 h-4" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-1">
                            <span className="font-semibold text-gray-900 text-sm">{log.action}</span>
                            {STATUS_BADGE[log.status]}
                          </div>
                          <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500">
                            {log.actorName && <span>{log.actorName}</span>}
                            {log.actorEmail && <span className="text-gray-400">{log.actorEmail}</span>}
                            {log.companyName && <span>· {log.companyName}</span>}
                            {log.ipAddress && (
                              <span className="flex items-center gap-1">
                                <Shield className="w-3 h-3" /> {log.ipAddress}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="text-right ml-4 flex-shrink-0">
                          <div className="text-sm font-medium text-gray-900">{time}</div>
                          <div className="text-xs text-gray-500">{date}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200">
            <div className="text-sm text-gray-600">
              Showing <span className="font-medium">{startIndex}</span>–<span className="font-medium">{endIndex}</span> of{' '}
              <span className="font-medium">{totalElements.toLocaleString()}</span>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => fetchLogs(page - 1)}
                disabled={page === 0}
                className="px-3 py-1 border border-gray-300 text-gray-700 rounded hover:bg-gray-50 disabled:opacity-40"
              >
                Previous
              </button>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const p = Math.max(0, Math.min(page - 2, totalPages - 5)) + i;
                return (
                  <button
                    key={p}
                    onClick={() => fetchLogs(p)}
                    className={`px-3 py-1 rounded ${p === page ? 'bg-primary-600 text-white' : 'border border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                  >
                    {p + 1}
                  </button>
                );
              })}
              <button
                onClick={() => fetchLogs(page + 1)}
                disabled={page >= totalPages - 1}
                className="px-3 py-1 border border-gray-300 text-gray-700 rounded hover:bg-gray-50 disabled:opacity-40"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ActivityLog;
